// Thin lines and small marks laid out in screen space each frame: the axis ticks, the rule under the lands'
// names, the ink line of an age. One instanced draw call.

import {
  DoubleSide,
  GLSL3,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  PlaneGeometry,
  RawShaderMaterial,
} from "three";
import { EDITION } from "./glsl.ts";
import type { SharedUniforms } from "./stage.ts";

export interface RuleDraw {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  alpha: number;
  /** A pigment index; or -1 for ink (the default), -2 for paper, -3 for the dark of a shadow. */
  pigment?: number;
  /** Corner radius, px; half the height makes a pill, half of both a dot. */
  radius?: number;
}

export class Rules {
  readonly mesh: Mesh;
  private geometry: InstancedBufferGeometry;
  private capacity = 0;
  private queue: RuleDraw[] = [];

  constructor(shared: SharedUniforms, renderOrder = 50) {
    this.geometry = this.makeGeometry(512);
    const material = new RawShaderMaterial({
      glslVersion: GLSL3,
      uniforms: { ...shared },
      transparent: true,
      depthTest: false,
      depthWrite: false,
      // Screen space runs y down, which turns the quads over.
      side: DoubleSide,
      vertexShader: /* glsl */ `
        precision highp float;
        uniform vec2 uViewport;
        in vec3 position;
        in vec4 aRect;
        in vec3 aStyle; // alpha, pigment (-1 ink), radius
        out vec2 vLocal;
        flat out vec2 vSize;
        flat out vec3 vStyle;
        void main() {
          vec2 p = mix(aRect.xy, aRect.zw, position.xy);
          vLocal = (position.xy - 0.5) * (aRect.zw - aRect.xy);
          vSize = aRect.zw - aRect.xy;
          vStyle = aStyle;
          gl_Position = vec4(p.x / uViewport.x * 2.0 - 1.0, 1.0 - p.y / uViewport.y * 2.0, 0.0, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        precision highp float;
        ${EDITION}
        in vec2 vLocal;
        flat in vec2 vSize;
        flat in vec3 vStyle;
        out vec4 fragColor;
        void main() {
          float r = min(vStyle.z, 0.5 * min(vSize.x, vSize.y));
          vec2 q = abs(vLocal) - (0.5 * vSize - r);
          float d = length(max(q, 0.0)) - r;
          // Shadows (pigment -3) are soft; everything else has a crisp edge.
          float a = vStyle.y < -2.5 ? vStyle.x * (1.0 - smoothstep(-r, r * 0.6, d)) : vStyle.x * (r > 0.0 ? clamp(0.5 - d, 0.0, 1.0) : 1.0);
          vec3 c = vStyle.y > -0.5 ? pigment(vStyle.y) : vStyle.y > -1.5 ? inkTone() : vStyle.y > -2.5 ? mix(paperTone(), vec3(1.0), 0.35 * (1.0 - uNight)) : vec3(0.1, 0.07, 0.03);
          fragColor = vec4(c, a);
        }`,
    });
    this.mesh = new Mesh(this.geometry, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = renderOrder;
  }

  private makeGeometry(capacity: number): InstancedBufferGeometry {
    const quad = new PlaneGeometry(1, 1).translate(0.5, 0.5, 0);
    const g = new InstancedBufferGeometry();
    g.index = quad.index;
    g.setAttribute("position", quad.getAttribute("position"));
    g.setAttribute("aRect", new InstancedBufferAttribute(new Float32Array(capacity * 4), 4).setUsage(35048));
    g.setAttribute("aStyle", new InstancedBufferAttribute(new Float32Array(capacity * 3), 3).setUsage(35048));
    g.instanceCount = 0;
    this.capacity = capacity;
    return g;
  }

  draw(r: RuleDraw): void {
    if (r.alpha > 0.004) this.queue.push(r);
  }

  flush(): void {
    const n = this.queue.length;
    if (n > this.capacity) {
      this.geometry.dispose();
      this.geometry = this.makeGeometry(Math.ceil(n * 1.5));
      this.mesh.geometry = this.geometry;
    }
    const rect = this.geometry.getAttribute("aRect") as InstancedBufferAttribute;
    const style = this.geometry.getAttribute("aStyle") as InstancedBufferAttribute;
    const R = rect.array as Float32Array,
      S = style.array as Float32Array;
    this.queue.forEach((r, k) => {
      R.set([r.x0, r.y0, r.x1, r.y1], k * 4);
      S.set([r.alpha, r.pigment ?? -1, r.radius ?? 0], k * 3);
    });
    this.geometry.instanceCount = n;
    rect.needsUpdate = style.needsUpdate = true;
    this.queue = [];
  }
}
